import React, { Component } from "react";
import { AnimatePresence, motion } from "framer-motion";
import UploadFiles from "./UploadFiles";
import RenderOtherFiles from "./RenderOtherFiles";
import AuditLog from "./AuditLog";
import FileUploader from "./FileUploader";
import FolderUploader from "./FolderUploader";
import UploadOptions from "./UplodOptions";
import UploadProgressTracker from "./UploadProgressTracker";
import { GetUserAction } from "../../../Actions/Authentication";
import { fetchProcessingStatus } from "../../../Actions/KnowledgeBaseBuilder";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";

const TAB_PATHS = {
  "knowledge-base": "/dashboard/file-manager/knowledge-base",
  "other-files": "/dashboard/file-manager/other-files",
  "audit-log": "/dashboard/file-manager/audit-log",
};

const getTabFromPath = (pathname) => {
  if (pathname.endsWith("/other-files")) return "other-files";
  if (pathname.endsWith("/audit-log")) return "audit-log";
  return "knowledge-base";
};

const TAB_BTN_BASE =
  "px-4 py-2 rounded-lg text-sm font-inter font-semibold whitespace-nowrap transition-all duration-150 touch-manipulation active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white";

class FileManagerHome extends Component {
  state = {
    showFileUploader: false,
    showFolderUploader: false,
    showOptions: false,
    ingestProgress: null, // { status, stage, file_id, file_name, error }
  };

  _socket = null;

  static propTypes = {
    GetUserAction: PropTypes.func.isRequired,
    fetchProcessingStatus: PropTypes.func.isRequired,
    user: PropTypes.object,
    navigate: PropTypes.func,
    location: PropTypes.object,
  };

  handleShowAndHideFileUploader = () => {
    this.setState((prevState) => ({
      showFileUploader: !prevState.showFileUploader,
      showOptions: false,
    }));
  };

  handleShowAndHideFolderUploader = () => {
    this.setState((prevState) => ({
      showFolderUploader: !prevState.showFolderUploader,
      showOptions: false,
    }));
  };

  toggleOptions = () => {
    this.setState((prevState) => ({
      showOptions: !prevState.showOptions,
    }));
  };

  componentDidMount() {
    this.props.GetUserAction();
    this._checkProcessingStatus();
  }

  componentWillUnmount() {
    if (this._socket) {
      this._socket.close();
      this._socket = null;
    }
  }

  _checkProcessingStatus = () => {
    this.props.fetchProcessingStatus().then((data) => {
      const inProgress = data?.processing?.[0];
      if (inProgress) {
        this._openIngestSocket(inProgress);
      }
    });
  };

  _openIngestSocket = (fileRecord) => {
    const userId = this.props.user?.id;
    if (!userId) return;

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${protocol}://${window.location.host}/ws/file-ingest/${userId}/`);
    this._socket = ws;

    // Show the bar immediately at ingesting stage while we wait for WS events
    this.setState({
      ingestProgress: {
        status: "PROCESSING",
        stage: "ingesting",
        file_id: fileRecord.file_id,
        file_name: fileRecord.file_name,
      },
    });

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        this.setState({ ingestProgress: data });
        if (data.status === "COMPLETED") {
          ws.close();
          this._socket = null;
          setTimeout(() => this.setState({ ingestProgress: null }), 3000);
        }
        if (data.status === "FAILED") {
          ws.close();
          this._socket = null;
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => { ws.close(); this._socket = null; };
  };

  render() {
    const { user, location, navigate } = this.props;
    const isStaff = user?.is_staff || user?.is_superuser;
    const activeTab = getTabFromPath(location?.pathname || "");
    const { ingestProgress } = this.state;

    return (
      <div
        className="relative w-full h-full flex flex-col border-l"
        style={{
          background:
            "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
        }}
      >
        {this.state.showFileUploader && isStaff && (
          <FileUploader
            handleShowAndHideFileUploader={this.handleShowAndHideFileUploader}
          />
        )}
        {this.state.showFolderUploader && isStaff && (
          <FolderUploader
            handleShowAndHideFolderUploader={
              this.handleShowAndHideFolderUploader
            }
          />
        )}

        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
          {/* Header row */}
          <div className="flex items-center gap-6">
            <h1 className="font-SFPro font-bold text-2xl text-gray-900 whitespace-nowrap">
              All Files
            </h1>

            {/* Tabs — segmented control, gradient matches Upload File */}
            <div
              className="inline-flex items-center gap-2 rounded-xl p-1.5 bg-gradient-to-r from-sky-50/95 via-blue-50/90 to-indigo-50/85 border border-blue-100/70 shadow-sm"
              role="tablist"
              aria-label="File manager sections"
            >
              {isStaff && (
                <>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "knowledge-base"}
                    onClick={() => navigate(TAB_PATHS["knowledge-base"])}
                    className={`${TAB_BTN_BASE} ${
                      activeTab === "knowledge-base"
                        ? "bg-gradient-to-r from-primary-blue to-blue-400 text-white shadow-sm hover:from-blue-600 hover:to-blue-500"
                        : "text-gray-600 hover:bg-white/80 hover:text-gray-900 hover:scale-[1.01]"
                    }`}
                  >
                    Knowledge Base Files
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "other-files"}
                    onClick={() => navigate(TAB_PATHS["other-files"])}
                    className={`${TAB_BTN_BASE} ${
                      activeTab === "other-files"
                        ? "bg-gradient-to-r from-primary-blue to-blue-400 text-white shadow-sm hover:from-blue-600 hover:to-blue-500"
                        : "text-gray-600 hover:bg-white/80 hover:text-gray-900 hover:scale-[1.01]"
                    }`}
                  >
                    Other Files
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "audit-log"}
                    onClick={() => navigate(TAB_PATHS["audit-log"])}
                    className={`${TAB_BTN_BASE} ${
                      activeTab === "audit-log"
                        ? "bg-gradient-to-r from-primary-blue to-blue-400 text-white shadow-sm hover:from-blue-600 hover:to-blue-500"
                        : "text-gray-600 hover:bg-white/80 hover:text-gray-900 hover:scale-[1.01]"
                    }`}
                  >
                    Audit Log
                  </button>
                </>
              )}
              {!isStaff && (
                <span
                  className="px-4 py-2 rounded-lg text-sm font-inter font-semibold whitespace-nowrap bg-gradient-to-r from-primary-blue to-blue-400 text-white shadow-sm"
                  role="tab"
                  aria-selected
                >
                  Other Files
                </span>
              )}
            </div>

            {/* Upload button */}
            <div className="ml-auto">
              {isStaff && activeTab === "knowledge-base" && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={this.toggleOptions}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-blue to-blue-400 text-white font-inter font-semibold text-sm rounded-xl hover:from-blue-600 hover:to-blue-500 transition-all duration-150 shadow-sm whitespace-nowrap"
                  >
                    Upload File
                  </button>
                  {this.state.showOptions && (
                    <UploadOptions
                      handleShowAndHideFileUploader={this.handleShowAndHideFileUploader}
                      handleShowAndHideFolderUploader={this.handleShowAndHideFolderUploader}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Progress banner */}
          {ingestProgress && !this.state.showFileUploader && !this.state.showFolderUploader && (
            <div className={`px-4 py-2.5 rounded-xl border ${
              ingestProgress.status === "COMPLETED" ? "bg-green-50 border-green-200" :
              ingestProgress.status === "FAILED"    ? "bg-red-50 border-red-200"    :
                                                      "bg-blue-50/60 border-blue-100"
            }`}>
              <UploadProgressTracker
                fileName={ingestProgress.file_name}
                status={ingestProgress.status}
                stage={ingestProgress.stage}
                error={ingestProgress.error}
              />
            </div>
          )}

          {/* Tab Content — quick fade on switch */}
          <AnimatePresence mode="wait">
            <motion.div
              key={isStaff ? activeTab : "other-files"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              {isStaff && activeTab === "knowledge-base" && (
                <UploadFiles handleShowAndHideFileUploader={this.handleShowAndHideFileUploader} />
              )}
              {(activeTab === "other-files" || !isStaff) && <RenderOtherFiles />}
              {isStaff && activeTab === "audit-log" && <AuditLog />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.authentication.user,
});

export default withRouterHooks(
  connect(mapStateToProps, { GetUserAction, fetchProcessingStatus })(FileManagerHome)
);
